import { useState, useEffect, useRef } from "react";
import { studyProcess, getStudyStatus, StudyAssetStatus } from "@/api/studio/uploads/api";
import { useEditorStore } from "@/features/studies/stores/use-editor-store";
import { AttentionReportsSkeleton } from "@/features/reports/components/attention-reports-skeleton";
import { StudiesLayout } from "@/components/layouts";
import { FullPageLoader } from "@/components/fallbacks";
import { useStudyNavigation } from "@/features/studies/hooks/use-study-navigation";
import { Progress } from "@/components/ui/progress";

/* Constants */
const POLL_INTERVAL = 3000;

/* Prop Types */
interface StudySceneProcessRouteProps {
  projectId: string;
  studyId: string;
}

/**
 * StudySceneProcessRoute component
 *
 * Triggers study-level processing and polls until every scene reaches a terminal state.
 */
export const StudySceneProcessRoute = ({ projectId, studyId }: StudySceneProcessRouteProps) => {
  /* State */
  const [assetStatuses, setAssetStatuses] = useState<StudyAssetStatus[]>([]);
  const [isActive, setIsActive] = useState(false);
  const hasStarted = useRef(false);

  /* Hooks */
  const { navigateToNext } = useStudyNavigation(projectId, studyId, "study-process");

  /* Actions */
  const { setContextPackIds } = useEditorStore((state) => state.editorActions);

  /* Derived state */
  const succeededCount = assetStatuses.filter((a) => a.status === "SUCCEEDED").length;
  const totalCount = assetStatuses.length;

  /* Side Effects */
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const startProcessing = async () => {
      setIsActive(true);

      await studyProcess(projectId, studyId);

      // Poll until all assets reach a terminal state (SUCCEEDED or FAILED)
      while (true) {
        const statuses = await getStudyStatus(projectId, studyId, "process");
        setAssetStatuses(statuses);

        const isFinished = (status: string) => status === "SUCCEEDED" || status === "FAILED";
        if (statuses.length > 0 && statuses.every((a) => isFinished(a.status))) {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      }

      // Reset the context pack IDs in the editor store
      setContextPackIds([]);

      navigateToNext();
    };

    void startProcessing();
  }, []);

  /* Render */
  return (
    <StudiesLayout projectId={projectId} studyId={studyId} currentStep="reports">
      {isActive && (
        <FullPageLoader>
          <div className="flex flex-col justify-center gap-2 items-center">
            <video src="/videos/process-animation.webm" className="w-48 pb-2" autoPlay muted loop />
            <h2>
              Processing {succeededCount} of {totalCount || "..."}
            </h2>
            {totalCount > 0 && <Progress value={(succeededCount / totalCount) * 100} staticColorClass="bg-success" className="w-1/2" />}
            <p>Analyzing scores...</p>
          </div>
        </FullPageLoader>
      )}
      <AttentionReportsSkeleton title="Attention" />
    </StudiesLayout>
  );
};
